// Authentication debugging utility to trace null reference errors

class AuthDebugger {
  constructor() {
    this.logs = [];
    this.maxLogs = 100;
    this.enabled = process.env.NODE_ENV === 'development';
  }

  log(message, data = null, level = 'info') {
    if (!this.enabled) return;
    
    const logEntry = {
      timestamp: new Date().toISOString(),
      message,
      data: data ? JSON.parse(JSON.stringify(data)) : null,
      level,
      stack: new Error().stack
    };
    
    this.logs.push(logEntry);
    
    // Keep only the last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }
  }

  error(message, data = null) {
    this.log(message, data, 'error');
  }

  warn(message, data = null) {
    this.log(message, data, 'warn');
  }

  info(message, data = null) {
    this.log(message, data, 'info');
  }

  // Validate user data with minimal overhead
  validateUserData(userData, context = 'unknown') {
    // Lightweight validation for both production and development
    if (userData === null || userData === undefined) {
      return { valid: false, issues: ['User data is null or undefined'] };
    }
    
    if (typeof userData !== 'object' || Array.isArray(userData)) {
      return { valid: false, issues: ['User data is not a valid object'] };
    }
    
    if (!userData.hasOwnProperty('role') || typeof userData.role === 'undefined' || userData.role === null) {
      return { valid: false, issues: ['User role is missing or invalid'] };
    }
    
    return { valid: true, issues: [], userData };
  }

  // Safe property access without logging
  safeGetUserRole(userData, context = 'unknown', defaultValue = 0) {
    // Production safe access without logging
    if (!userData || typeof userData !== 'object' || 
        typeof userData.role === 'undefined' || userData.role === null) {
      return defaultValue;
    }
    
    let role = userData.role;
    if (typeof role === 'string' && !isNaN(role)) {
      role = parseInt(role, 10);
    }
    
    return typeof role === 'number' && !isNaN(role) ? role : defaultValue;
  }

  // Simple proxy creation without logging
  createSafeUserDataProxy(userData, context = 'unknown') {
    return userData; // Just return the data as-is
  }

  // Minimal methods for compatibility
  getRecentLogs(count = 20) {
    return this.enabled ? this.logs.slice(-count) : [];
  }

  getErrorLogs() {
    return this.enabled ? this.logs.filter(log => log.level === 'error') : [];
  }

  clearLogs() {
    if (this.enabled) {
      this.logs = [];
    }
  }

  generateReport() {
    return this.enabled ? {
      timestamp: new Date().toISOString(),
      totalLogs: this.logs.length,
      errorCount: this.getErrorLogs().length
    } : null;
  }

  getCommonErrors() {
    return this.enabled ? [] : [];
  }
}

// Create singleton instance
const authDebugger = new AuthDebugger();

// Make it globally available for debugging (only in development)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.authDebugger = authDebugger;
}

export default authDebugger;